import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { api } from '../../App';
import { Building2, GraduationCap, Users, BarChart3, Plus, Eye, UserCog, Shield } from 'lucide-react';

export default function EducationTab({ 
  educationStats, 
  institutions, 
  onRefresh,
  onImpersonate 
}) {
  const [institutionDialogOpen, setInstitutionDialogOpen] = useState(false);
  const [teacherDialogOpen, setTeacherDialogOpen] = useState(false);
  const [bulkStudentDialogOpen, setBulkStudentDialogOpen] = useState(false);
  const [institutionDetailOpen, setInstitutionDetailOpen] = useState(false);
  const [selectedInstitution, setSelectedInstitution] = useState(null);
  const [institutionDetails, setInstitutionDetails] = useState(null);
  const [bulkUsersText, setBulkUsersText] = useState('');

  const [institutionForm, setInstitutionForm] = useState({
    name: '',
    type: 'K12',
    city: '',
    address: '',
    admin_name: '',
    admin_email: '',
    admin_password: ''
  });

  const [teacherForm, setTeacherForm] = useState({
    name: '',
    email: '',
    password: '',
    institution_id: '',
    role: 'teacher',
    title: ''
  });

  const handleCreateInstitution = async () => {
    if (!institutionForm.name || !institutionForm.admin_email) {
      toast.error('Kurum adı ve admin email zorunludur');
      return;
    }

    try {
      await api.post('/institutions', institutionForm);
      toast.success('Kurum ve admin oluşturuldu');
      setInstitutionDialogOpen(false);
      setInstitutionForm({
        name: '', type: 'K12', city: '', address: '',
        admin_name: '', admin_email: '', admin_password: ''
      });
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kurum oluşturulamadı');
    }
  };

  const handleCreateTeacher = async () => {
    if (!teacherForm.name || !teacherForm.email || !teacherForm.institution_id) {
      toast.error('Ad, email ve kurum seçimi zorunludur');
      return;
    }

    try {
      await api.post('/teachers', teacherForm);
      toast.success('Personel oluşturuldu');
      setTeacherDialogOpen(false);
      setTeacherForm({ name: '', email: '', password: '', institution_id: '', role: 'teacher', title: '' });
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Personel oluşturulamadı');
    }
  };

  const handleBulkStudents = async () => {
    if (!bulkUsersText.trim() || !teacherForm.institution_id) {
      toast.error('Kurum seçimi ve öğrenci listesi zorunludur');
      return;
    }

    try {
      const students = bulkUsersText.trim().split('\n').map(line => {
        const [name, email] = line.split(',').map(s => s.trim());
        return { name, email, password: 'Ogrenci123!' };
      }).filter(s => s.name && s.email);

      await api.post('/institutions/bulk-add-students', {
        institution_id: teacherForm.institution_id,
        students
      });

      toast.success(`${students.length} öğrenci eklendi`);
      setBulkStudentDialogOpen(false);
      setBulkUsersText('');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Öğrenciler eklenemedi');
    }
  };

  const handleViewInstitution = async (institution) => {
    try {
      setSelectedInstitution(institution);
      setInstitutionDetails(null);
      setInstitutionDetailOpen(true);
      const response = await api.get(`/institutions/${institution.id}/detail`);
      const data = response.data;
      setInstitutionDetails({
        ...data.institution,
        admin: institution.admin,
        staff: data.staff || [],
        classes: data.classes || [],
        student_count: data.stats?.total_students || 0,
        report_count: data.stats?.total_reports || 0
      });
    } catch (error) {
      console.error('Failed to fetch institution details:', error);
      toast.error('Kurum detayları yüklenemedi');
      setInstitutionDetailOpen(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid md:grid-cols-4 gap-6">
        <Card className="shadow-lg border-none bg-gradient-to-br from-blue-50 to-cyan-50">
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-blue-600" />
              Toplam Kurum
            </CardDescription>
            <CardTitle className="text-3xl text-blue-600">{educationStats.total_institutions}</CardTitle>
          </CardHeader>
        </Card>
        <Card className="shadow-lg border-none bg-gradient-to-br from-emerald-50 to-green-50">
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <GraduationCap className="w-4 h-4 text-emerald-600" />
              Toplam Öğretmen
            </CardDescription>
            <CardTitle className="text-3xl text-emerald-600">{educationStats.total_teachers}</CardTitle>
          </CardHeader>
        </Card>
        <Card className="shadow-lg border-none bg-gradient-to-br from-purple-50 to-pink-50">
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <Users className="w-4 h-4 text-purple-600" />
              Toplam Öğrenci
            </CardDescription>
            <CardTitle className="text-3xl text-purple-600">{educationStats.total_students}</CardTitle>
          </CardHeader>
        </Card>
        <Card className="shadow-lg border-none bg-gradient-to-br from-orange-50 to-amber-50">
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-orange-600" />
              Aktif Sınıf
            </CardDescription>
            <CardTitle className="text-3xl text-orange-600">{educationStats.total_classes}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Management Card */}
      <Card className="shadow-lg border-none">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-blue-600" />
            Eğitim Platformu Yönetimi
          </CardTitle>
          <CardDescription>Kurumları, öğretmenleri ve sınıfları yönetin</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Action Buttons */}
            <div className="flex gap-4">
              <Dialog open={institutionDialogOpen} onOpenChange={setInstitutionDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-gradient-to-r from-blue-600 to-cyan-600">
                    <Building2 className="w-4 h-4 mr-2" />
                    Kurum Ekle
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Yeni Kurum ve Admin Ekle</DialogTitle>
                    <DialogDescription>Kurum bilgilerini ve kurum admin bilgilerini girin</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="border-b pb-4">
                      <h3 className="font-semibold mb-3">Kurum Bilgileri</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Kurum Adı *</Label>
                          <Input
                            value={institutionForm.name}
                            onChange={(e) => setInstitutionForm({...institutionForm, name: e.target.value})}
                            placeholder="Örn: Atatürk Lisesi"
                          />
                        </div>
                        <div>
                          <Label>Kurum Tipi</Label>
                          <select
                            className="w-full border rounded-md px-3 py-2"
                            value={institutionForm.type}
                            onChange={(e) => setInstitutionForm({...institutionForm, type: e.target.value})}
                          >
                            <option value="K12">K12</option>
                            <option value="anaokulu">Anaokulu</option>
                            <option value="lise">Lise</option>
                            <option value="kolej">Kolej</option>
                          </select>
                        </div>
                        <div>
                          <Label>Şehir</Label>
                          <Input
                            value={institutionForm.city}
                            onChange={(e) => setInstitutionForm({...institutionForm, city: e.target.value})}
                            placeholder="Örn: İstanbul"
                          />
                        </div>
                        <div className="col-span-2">
                          <Label>Adres</Label>
                          <Textarea
                            value={institutionForm.address}
                            onChange={(e) => setInstitutionForm({...institutionForm, address: e.target.value})}
                            placeholder="Tam adres"
                            rows={2}
                          />
                        </div>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-3">Admin Bilgileri</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Admin Adı *</Label>
                          <Input
                            value={institutionForm.admin_name}
                            onChange={(e) => setInstitutionForm({...institutionForm, admin_name: e.target.value})}
                            placeholder="Örn: Ahmet Yılmaz"
                          />
                        </div>
                        <div>
                          <Label>Admin Email *</Label>
                          <Input
                            type="email"
                            value={institutionForm.admin_email}
                            onChange={(e) => setInstitutionForm({...institutionForm, admin_email: e.target.value})}
                            placeholder="admin@okul.com"
                          />
                        </div>
                        <div className="col-span-2">
                          <Label>Şifre (boş bırakılırsa: Admin123!)</Label>
                          <Input
                            type="password"
                            value={institutionForm.admin_password}
                            onChange={(e) => setInstitutionForm({...institutionForm, admin_password: e.target.value})}
                            placeholder="Şifre"
                          />
                        </div>
                      </div>
                    </div>
                    <Button onClick={handleCreateInstitution} className="w-full bg-gradient-to-r from-blue-600 to-cyan-600">
                      Kurum ve Admin Oluştur
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              <Dialog open={teacherDialogOpen} onOpenChange={setTeacherDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-emerald-200 bg-emerald-50">
                    <GraduationCap className="w-4 h-4 mr-2" />
                    Personel Ekle
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Personel Ekle</DialogTitle>
                    <DialogDescription>Kurum için yeni personel ekleyin</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Kurum Seçin *</Label>
                      <select
                        className="w-full border rounded-md px-3 py-2"
                        value={teacherForm.institution_id}
                        onChange={(e) => setTeacherForm({...teacherForm, institution_id: e.target.value})}
                      >
                        <option value="">Kurum seçin...</option>
                        {institutions.map(inst => (
                          <option key={inst.id} value={inst.id}>{inst.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>Rol *</Label>
                      <select
                        className="w-full border rounded-md px-3 py-2"
                        value={teacherForm.role}
                        onChange={(e) => setTeacherForm({...teacherForm, role: e.target.value})}
                      >
                        <option value="teacher">Öğretmen</option>
                        <option value="mudur">Müdür</option>
                        <option value="mudur_yardimcisi">Müdür Yardımcısı</option>
                        <option value="psikolog">Psikolog</option>
                        <option value="rehber_ogretmen">Rehber Öğretmen (PDR)</option>
                      </select>
                    </div>
                    <div>
                      <Label>Ad Soyad *</Label>
                      <Input
                        value={teacherForm.name}
                        onChange={(e) => setTeacherForm({...teacherForm, name: e.target.value})}
                        placeholder="Örn: Ayşe Kaya"
                      />
                    </div>
                    <div>
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={teacherForm.email}
                        onChange={(e) => setTeacherForm({...teacherForm, email: e.target.value})}
                        placeholder="ogretmen@okul.com"
                      />
                    </div>
                    <div>
                      <Label>Şifre (boş bırakılırsa: Test123!)</Label>
                      <Input
                        type="password"
                        value={teacherForm.password}
                        onChange={(e) => setTeacherForm({...teacherForm, password: e.target.value})}
                      />
                    </div>
                    <Button onClick={handleCreateTeacher} className="w-full bg-gradient-to-r from-emerald-600 to-green-600">
                      Personel Ekle
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              <Dialog open={bulkStudentDialogOpen} onOpenChange={setBulkStudentDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-purple-200 bg-purple-50">
                    <Users className="w-4 h-4 mr-2" />
                    Toplu Öğrenci Ekle
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Toplu Öğrenci Ekle</DialogTitle>
                    <DialogDescription>Her satıra bir öğrenci: Ad Soyad, Email</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Kurum Seçin *</Label>
                      <select
                        className="w-full border rounded-md px-3 py-2"
                        value={teacherForm.institution_id}
                        onChange={(e) => setTeacherForm({...teacherForm, institution_id: e.target.value})}
                      >
                        <option value="">Kurum seçin...</option>
                        {institutions.map(inst => (
                          <option key={inst.id} value={inst.id}>{inst.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>Öğrenci Listesi</Label>
                      <Textarea
                        value={bulkUsersText}
                        onChange={(e) => setBulkUsersText(e.target.value)}
                        placeholder="Ali Veli, ali@okul.com&#10;Ayşe Kaya, ayse@okul.com"
                        rows={8}
                      />
                    </div>
                    <Button onClick={handleBulkStudents} className="w-full bg-gradient-to-r from-purple-600 to-pink-600">
                      Öğrencileri Ekle
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {/* Institutions List */}
            {institutions.length > 0 ? (
              <div className="space-y-3 mt-6">
                <h3 className="font-semibold text-slate-700">Kayıtlı Kurumlar ({institutions.length})</h3>
                {institutions.map((inst) => (
                  <Card key={inst.id} className="border shadow-sm">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="font-semibold text-slate-800">{inst.name}</h4>
                          <div className="flex gap-4 mt-2 text-sm text-slate-600">
                            <span>📍 {inst.city || 'Şehir belirtilmemiş'}</span>
                            <span>🏫 {inst.type}</span>
                          </div>
                          <div className="flex gap-4 mt-2 text-sm">
                            <Badge variant="outline" className="bg-emerald-50">
                              <GraduationCap className="w-3 h-3 mr-1" />
                              {inst.teacher_count} Öğretmen
                            </Badge>
                            <Badge variant="outline" className="bg-purple-50">
                              <Users className="w-3 h-3 mr-1" />
                              {inst.student_count} Öğrenci
                            </Badge>
                            <Badge variant="outline" className="bg-blue-50">
                              {inst.class_count} Sınıf
                            </Badge>
                          </div>
                          {inst.admin && (
                            <div className="mt-2 text-sm text-slate-600 bg-slate-50 p-2 rounded">
                              <strong>Admin:</strong> {inst.admin.name} ({inst.admin.email})
                            </div>
                          )}
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleViewInstitution(inst)}
                          className="bg-blue-50 border-blue-200 hover:bg-blue-100"
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          Görüntüle
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="bg-slate-50 p-8 rounded-lg text-center mt-6">
                <Building2 className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                <p className="text-slate-600 mb-2">Henüz kurum eklenmemiş</p>
              </div>
            )}

            {/* Institution Detail Modal */}
            <Dialog open={institutionDetailOpen} onOpenChange={setInstitutionDetailOpen}>
              <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-blue-600" />
                    {selectedInstitution?.name || 'Kurum Detayları'}
                  </DialogTitle>
                  <DialogDescription>
                    {selectedInstitution?.city && `📍 ${selectedInstitution.city}`} 
                    {selectedInstitution?.type && ` | 🏫 ${selectedInstitution.type}`}
                  </DialogDescription>
                </DialogHeader>
                
                {institutionDetails ? (
                  <div className="space-y-6 mt-4">
                    {/* Stats Overview */}
                    <div className="grid grid-cols-4 gap-4">
                      <div className="bg-blue-50 p-4 rounded-lg text-center">
                        <p className="text-2xl font-bold text-blue-600">{institutionDetails.staff?.length || 0}</p>
                        <p className="text-sm text-slate-600">Personel</p>
                      </div>
                      <div className="bg-emerald-50 p-4 rounded-lg text-center">
                        <p className="text-2xl font-bold text-emerald-600">{institutionDetails.student_count || 0}</p>
                        <p className="text-sm text-slate-600">Öğrenci</p>
                      </div>
                      <div className="bg-purple-50 p-4 rounded-lg text-center">
                        <p className="text-2xl font-bold text-purple-600">{institutionDetails.classes?.length || 0}</p>
                        <p className="text-sm text-slate-600">Sınıf</p>
                      </div>
                      <div className="bg-orange-50 p-4 rounded-lg text-center">
                        <p className="text-2xl font-bold text-orange-600">{institutionDetails.report_count || 0}</p>
                        <p className="text-sm text-slate-600">Rapor</p>
                      </div>
                    </div>

                    {/* Admin Info */}
                    {institutionDetails.admin && (
                      <div className="bg-gradient-to-r from-slate-50 to-blue-50 p-4 rounded-lg border">
                        <h4 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                          <Shield className="w-4 h-4 text-blue-600" />
                          Kurum Yöneticisi
                        </h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm text-slate-500">Ad Soyad</p>
                            <p className="font-medium">{institutionDetails.admin.name}</p>
                          </div>
                          <div>
                            <p className="text-sm text-slate-500">Email</p>
                            <p className="font-medium">{institutionDetails.admin.email}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Staff List */}
                    <div>
                      <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                        <GraduationCap className="w-4 h-4 text-emerald-600" />
                        Personel Listesi ({institutionDetails.staff?.length || 0})
                      </h4>
                      {institutionDetails.staff && institutionDetails.staff.length > 0 ? (
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {institutionDetails.staff.map((staff, idx) => (
                            <div key={idx} className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                              <div>
                                <p className="font-medium text-slate-800">{staff.name}</p>
                                <p className="text-sm text-slate-500">{staff.email}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                {staff.title && <Badge variant="outline" className="text-xs">{staff.title}</Badge>}
                                <Badge className={
                                  staff.role === 'mudur' ? 'bg-red-100 text-red-700' :
                                  staff.role === 'psikolog' ? 'bg-purple-100 text-purple-700' :
                                  staff.role === 'rehber_ogretmen' ? 'bg-blue-100 text-blue-700' :
                                  'bg-emerald-100 text-emerald-700'
                                }>
                                  {staff.role === 'teacher' ? 'Öğretmen' :
                                   staff.role === 'mudur' ? 'Müdür' :
                                   staff.role === 'mudur_yardimcisi' ? 'Müdür Yrd.' :
                                   staff.role === 'psikolog' ? 'Psikolog' :
                                   staff.role === 'rehber_ogretmen' ? 'PDR' : staff.role}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-500 bg-slate-50 p-4 rounded-lg text-center">
                          Henüz personel eklenmemiş
                        </p>
                      )}
                    </div>

                    {/* Classes List */}
                    <div>
                      <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                        <BarChart3 className="w-4 h-4 text-purple-600" />
                        Sınıflar ({institutionDetails.classes?.length || 0})
                      </h4>
                      {institutionDetails.classes && institutionDetails.classes.length > 0 ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {institutionDetails.classes.map((cls, idx) => (
                            <div key={idx} className="p-3 bg-purple-50 rounded-lg border border-purple-100">
                              <p className="font-medium text-purple-800">{cls.name}</p>
                              <div className="flex gap-2 mt-2 text-xs">
                                <Badge variant="outline">{cls.grade_level}. Sınıf</Badge>
                                <Badge variant="outline">{cls.student_count || 0} Öğrenci</Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-500 bg-slate-50 p-4 rounded-lg text-center">
                          Henüz sınıf oluşturulmamış
                        </p>
                      )}
                    </div>

                    {/* Quick Actions */}
                    <div className="flex gap-3 pt-4 border-t">
                      <Button 
                        variant="outline"
                        onClick={() => {
                          setTeacherForm({...teacherForm, institution_id: selectedInstitution?.id});
                          setInstitutionDetailOpen(false);
                          setTeacherDialogOpen(true);
                        }}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Personel Ekle
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => onImpersonate(institutionDetails.admin?.id)}
                        disabled={!institutionDetails.admin}
                        className="bg-purple-50 border-purple-200"
                      >
                        <UserCog className="w-4 h-4 mr-2" />
                        Admin Olarak Gir
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
